import React, { useState, useEffect, useRef, useCallback } from 'react';
import LocationList from '../../LocationList';
import ModeHelpTooltip from './components/ModeHelpTooltip';
import PathSequence from './components/PathSequence';
import AvailablePoints from './components/AvailablePoints';
import PathDistance from './components/PathDistance';
import PathActions from './components/PathActions';
import { findPointById } from './utils/pathOperations';
import { areAllPointsSelected } from './utils/pathOperations';
import { isCustomMode, calculatePathDistance } from '../../../Services';
import { saveCustomPathToPermanent } from '../../../Services/UtilityServices/SessionService';
import { debug } from '../../../../../utils/debug';
import { useTsp } from '../../../context/TspContext';
import '../../styles/modes/customMode.css';

export default function CustomMode() {

  const { 
    locations, 
    startId, 
    setLocations, 
    setStartId, 
    setPath,
    customPath: contextCustomPath, 
    setMapPointSelect,
    currentModeRef
  } = useTsp();
  
  // Initialize customPath using passed path as initial value
  const [customPath, setCustomPath] = useState(() => {
    if (contextCustomPath && contextCustomPath.length > 0) {
      const pathObjects = contextCustomPath.map(id => {
        const loc = locations.find(l => l.id === id);
        return loc || null;
      }).filter(Boolean);
      
      if (pathObjects.length > 0) {
        return pathObjects;
      }
    }
    return [];
  });
  
  const [availablePoints, setAvailablePoints] = useState([]);
  // Initialize pathComplete based on whether the path is confirmed, not just length
  const [pathComplete, setPathComplete] = useState(
    contextCustomPath && contextCustomPath.length > 1 && contextCustomPath._isConfirmed
  );
  const [pathConfirmed, setPathConfirmed] = useState(
    (contextCustomPath && contextCustomPath._isConfirmed) || false
  );
  const [initialized, setInitialized] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Use ref to track external path and update status
  const pathRef = useRef(contextCustomPath);
  const isUpdatingRef = useRef(false);
  const customPathRef = useRef(customPath);
  
  // Toggle help tooltip
  const toggleHelp = (e) => {
    e.preventDefault();
    setShowHelp(!showHelp);
  };
  
  // Update reference values
  useEffect(() => {
    pathRef.current = contextCustomPath;
    customPathRef.current = customPath;
  }, [contextCustomPath, customPath]);
  
  // Throttled path update function - optimized with useCallback
  const updatePathThrottled = useCallback((newPathIds) => {
    if (isUpdatingRef.current || 
        (pathRef.current.length === newPathIds.length && 
         pathRef.current.every((id, i) => id === newPathIds[i]))) {
      return;
    }
    
    // Ensure we only update path in custom path mode
    if (currentModeRef && isCustomMode(currentModeRef.current)) {
      isUpdatingRef.current = true;
      pathRef.current = newPathIds;
      setPath(newPathIds);
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 300);
    }
  }, [setPath, currentModeRef]);
  
  // Select next path point - optimized with useCallback
  const selectNextPoint = useCallback((point) => {
    const isAlreadyInPath = customPathRef.current.some(p => p.id === point.id);
    
    if (isAlreadyInPath) {
      const index = customPathRef.current.findIndex(p => p.id === point.id);
      setCustomPath(prev => prev.slice(0, index + 1));
      setPathComplete(false);
    } else {
      setCustomPath(prev => [...prev, point]);
      
      // Don't automatically set pathComplete to true
      // Path should only be marked complete when user confirms it
    }
  }, []);
  
  // Sync with context custom path when switching back to custom mode
  useEffect(() => {
    if (contextCustomPath && contextCustomPath.length > 0) {
      // Convert ID array back to object array
      const pathObjects = contextCustomPath.map(id => {
        const loc = locations.find(l => l.id === id);
        return loc || null;
      }).filter(Boolean);
      
      if (pathObjects.length > 0) {
        setCustomPath(pathObjects);
        // Only mark as complete if it's confirmed
        setPathComplete(contextCustomPath._isConfirmed || false);
        setPathConfirmed(contextCustomPath._isConfirmed || false);
      }
    } else if (contextCustomPath && contextCustomPath.length === 0) {
      // Clear local state if context is empty
      setCustomPath([]);
      setPathComplete(false);
      setPathConfirmed(false);
    }
  }, [contextCustomPath, locations]);

  // Initialize available points - only run on initial load and locations change
  useEffect(() => {
    if (locations.length > 0) {
      setAvailablePoints(locations.map(loc => ({ ...loc })));
      
      if (!initialized && customPath.length === 0) {
        setInitialized(true);
      }
    } else {
      setAvailablePoints([]);
      if (customPath.length > 0) {
        setCustomPath([]);
      }
    }
  }, [locations, initialized, customPath.length]);
  
  // Update path when start point changes
  useEffect(() => {
    if (locations.length === 0) {
      setCustomPath([]);
      return;
    }
    
    if (!startId) {
      setCustomPath([]);
      setPathComplete(false);
      return;
    }
    
    const startPoint = findPointById(locations, startId);
    
    if (!startPoint) {
      setCustomPath([]);
      setPathComplete(false);
      return;
    }
    
    // Only initialize with start point if path is empty
    if (customPath.length === 0) {
      setCustomPath([startPoint]);
      setPathComplete(false);
      return;
    }
    
    // If start point is already first in path, don't update
    if (customPath[0] && customPath[0].id === startId) {
      return;
    }
    
    // If start point changed and path exists, reorganize path
    const startPointIndexInPath = customPath.findIndex(p => p.id === startId);
    
    if (startPointIndexInPath > 0) {
      const newPath = [
        customPath[startPointIndexInPath],
        ...customPath.slice(startPointIndexInPath + 1),
        ...customPath.slice(0, startPointIndexInPath)
      ];
      setCustomPath(newPath);
    } else if (startPointIndexInPath === -1) {
      // New start point not in current path, reset to just start point
      setCustomPath([startPoint]);
    }
    
    setPathComplete(false);
  }, [startId, locations]);
  
  // Set map point selection callback - optimized with useCallback
  useEffect(() => {
    if (setMapPointSelect && currentModeRef && isCustomMode(currentModeRef.current)) {
      if (startId) {
        setMapPointSelect(selectNextPoint);
      } else {
        setMapPointSelect(null);
      }
    }
    
    return () => {
      if (setMapPointSelect) {
        setMapPointSelect(null);
      }
    };
  }, [startId, setMapPointSelect, selectNextPoint, currentModeRef]);
  
  // Reset path - optimized with useCallback
  const resetPath = useCallback(() => {
    if (startId) {
      const startPoint = findPointById(locations, startId);
      if (startPoint) {
        setCustomPath([startPoint]);
      } else {
        setCustomPath([]);
      }
    } else {
      setCustomPath([]);
    }
    
    // Clear path without confirmation flag
    const emptyPath = [];
    updatePathThrottled(emptyPath);
    setPathComplete(false);
    setPathConfirmed(false);
  }, [startId, locations, updatePathThrottled]);
  
  // Confirm path - optimized with useCallback
  const confirmPath = useCallback(async () => {
    // Check if all location points have been selected
    if (!areAllPointsSelected(locations, customPath)) {
      alert("Please ensure all location points are selected before confirming the path");
      return;
    }
    
    if (customPath.length > 1) {
      // Find start point object
      const startPoint = findPointById(locations, startId);
      
      // Check if path is already closed
      const isAlreadyClosed = customPath[0].id === startId && 
                             customPath[customPath.length - 1].id === startId;
      
      // Create updated path array
      let updatedPath = [...customPath];
      
      // If path is not closed and has start point, add start point to end
      if (!isAlreadyClosed && startPoint) {
        updatedPath = [...customPath, startPoint];
        setCustomPath(updatedPath);
      }
      
      // Extract ID array for update
      let pathIds = updatedPath.map(point => point.id);
      
      // Mark path as confirmed before updating
      const confirmedPathIds = [...pathIds];
      confirmedPathIds._isConfirmed = true;
      
      updatePathThrottled(confirmedPathIds);
      setPathComplete(true);
      setPathConfirmed(true);
      
      // Save to permanent storage
      try {
        const distance = calculatePathDistance(pathIds, locations);
        const result = await saveCustomPathToPermanent(pathIds, distance);
        debug('TSP-CUSTOM', 'Custom path saved successfully', { 
          pathLength: pathIds.length, 
          distance: distance.toFixed(2) + 'km',
          pathId: result.id 
        });
      } catch (error) {
        debug('TSP-CUSTOM', 'Failed to save custom path to permanent storage', error);
        // Continue anyway - path is still valid in memory
      }
    }
  }, [customPath, updatePathThrottled, locations, startId]);
  
  // Remove point from path - optimized with useCallback
  const removePointFromPath = useCallback((index) => {
    if (index === 0 && customPath[0].id === startId) {
      return;
    }
    
    setCustomPath(prev => {
      const newPath = [...prev];
      newPath.splice(index, 1);
      return newPath;
    });
    
    setPathComplete(false);
    setPathConfirmed(false);
  }, [customPath, startId]);
  
  // Monitor custom path changes, automatically update path display on map
  useEffect(() => {
    if (customPath.length > 1) {
      let pathIds = customPath.map(point => point.id);
      
      // 🔧 Fix: Only auto-close path when confirmed AND complete
      if (pathComplete && pathConfirmed && pathIds[0] && pathIds[0] === startId) {
        if (pathIds[0] !== pathIds[pathIds.length - 1]) {
          pathIds = [...pathIds, pathIds[0]];
        }
      }
      
      // Only update if different - temporary path without confirmation flag
      if (pathRef.current.length !== pathIds.length || 
          !pathRef.current.every((id, i) => id === pathIds[i])) {
        // Temporary path during editing (not confirmed)
        const tempPath = [...pathIds];
        // Do not add _isConfirmed flag for temporary paths
        updatePathThrottled(tempPath);
      }
    } else if (customPath.length <= 1 && pathRef.current.length > 0) {
      updatePathThrottled([]);
    }
  }, [customPath, startId, updatePathThrottled, pathComplete, pathConfirmed]);
  
  return (
    <div className="custom-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Custom Path</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Custom Path mode"
          >
            ?
          </button>
        </div>
        <p>You can manually define the order of visiting location points.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      <LocationList 
        locations={locations}
        startId={startId}
        mode="Custom Path"
        readOnly={false}
        showActions={true}
        allowDelete={false}
        allowSetStart={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
      />
      
      {locations.length > 0 ? (
        <div className="custom-path-builder">
          <h4>Custom Path Builder</h4>
          <p>Please select location points in order, two selection methods available: map selection and list selection.</p>
          
          <PathSequence 
            customPath={customPath}
            startId={startId}
            removePointFromPath={removePointFromPath}
          />
          
          <AvailablePoints 
            customPath={customPath}
            availablePoints={availablePoints}
            selectNextPoint={selectNextPoint}
            pathComplete={pathComplete}
          />
          
          <PathDistance customPath={customPath} />
          
          <PathActions 
            confirmPath={confirmPath}
            resetPath={resetPath}
            customPath={customPath}
            locationsLength={locations.length}
          />
        </div>
      ) : null}
    </div>
  );
} 